/**
 * Unified scroll-up reveal for announcement bar + header.
 *
 * Writes <html data-wd-sticky="top|visible|hidden"> based on scroll
 * position and direction. CSS in assets/wd-marketing.css reads that
 * attribute and positions/transforms BOTH the announcement-bar
 * .shopify-section and the header .shopify-section in lockstep.
 *
 * State machine:
 *   scrollY <= TOP_EPSILON               -> "top"     (natural position)
 *   scrollY >  TOP_EPSILON AND going down -> "hidden"  (fixed, translateY(-...))
 *   scrollY >  TOP_EPSILON AND going up   -> "visible" (fixed, translateY(0))
 *
 * We DON'T return to "top" mode while the user is scrolling up through
 * the threshold zone — only when scrollY actually reaches ~0. Otherwise
 * the bar+header would jump from fixed-at-viewport-top to absolute-at-
 * document-top mid-scroll, looking like they vanish until you reach the
 * actual top.
 *
 * Sets --wd-bar-height and --wd-header-height CSS vars from measured
 * heights so CSS can stack them correctly. Sets body padding-top when
 * entering fixed mode so the page below doesn't jump.
 *
 * DOM notes (Horizon theme):
 *   - Announcement bar section: .shopify-section.shopify-section-group-header-group
 *     with a direct child ASIDE.announcement-bar
 *   - Header section: .shopify-section.shopify-section-group-header-group.header-section
 *     (the class is ON the wrapper itself, not a child element)
 */
(function () {
  'use strict';

  var THRESHOLD = 120;     // scrolling DOWN past this enters fixed mode
  var TOP_EPSILON = 4;     // scrolling UP back to within this returns to natural
  var html = document.documentElement;
  var body = document.body;

  var lastScrollY = window.scrollY;
  var rafScheduled = false;
  // Use a sentinel so applyMode always writes the attribute on first call.
  var currentMode = null;
  var barHeight = 0;
  var headerHeight = 0;
  var els = null;

  function findElements() {
    // Bar: section wrapper that contains a direct-child .announcement-bar
    var barSection = document.querySelector(
      '.shopify-section.shopify-section-group-header-group:has(> .announcement-bar)'
    );
    // Header: section wrapper that itself carries the .header-section class
    // (Horizon places the class on the .shopify-section, not on a child)
    var headerSection = document.querySelector(
      '.shopify-section.shopify-section-group-header-group.header-section'
    );
    return { barSection: barSection, headerSection: headerSection };
  }

  function measureHeights() {
    if (els.barSection) {
      barHeight = els.barSection.getBoundingClientRect().height;
      html.style.setProperty('--wd-bar-height', barHeight + 'px');
    }
    if (els.headerSection) {
      headerHeight = els.headerSection.getBoundingClientRect().height;
      html.style.setProperty('--wd-header-height', headerHeight + 'px');
    }
  }

  function applyMode(mode) {
    if (mode === currentMode) return;
    var previousMode = currentMode;
    currentMode = mode;

    // When entering fixed mode from natural ('top') mode, suppress the
    // transform transition for one frame. Otherwise the bar+header animate
    // from translateY(0) to translateY(-fullheight) — which pops them back
    // into view at the top of the viewport, then slides them out. Looks
    // like a "double scroll up". Skipping the transition makes them snap
    // straight to off-screen, invisible to the user.
    var enteringFixed = previousMode === 'top' && (mode === 'hidden' || mode === 'visible');
    if (enteringFixed) {
      html.setAttribute('data-wd-sticky-instant', 'true');
    }

    html.setAttribute('data-wd-sticky', mode);

    if (mode === 'top') {
      body.style.paddingTop = '';
    } else {
      body.style.paddingTop = (barHeight + headerHeight) + 'px';
    }

    if (enteringFixed) {
      // Remove the instant flag after two rAFs so the just-applied
      // transform settles without animation, and subsequent state changes
      // animate normally.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          html.removeAttribute('data-wd-sticky-instant');
        });
      });
    }
  }

  function update() {
    rafScheduled = false;
    var scrollY = window.scrollY;
    var direction = scrollY > lastScrollY ? 'down' : 'up';

    var nextMode;
    if (scrollY <= TOP_EPSILON) {
      // Actually at the very top — return to natural position.
      nextMode = 'top';
    } else if (currentMode === 'top' && scrollY < THRESHOLD) {
      // Still inside the natural-position zone on the way down.
      // Don't go fixed yet.
      nextMode = 'top';
    } else if (direction === 'down') {
      nextMode = 'hidden';
    } else {
      nextMode = 'visible';
    }

    applyMode(nextMode);
    lastScrollY = scrollY;
  }

  function onScroll() {
    if (rafScheduled) return;
    rafScheduled = true;
    window.requestAnimationFrame(update);
  }

  function init() {
    els = findElements();
    if (!els.barSection && !els.headerSection) return;
    // Measure heights BEFORE setting the initial mode so CSS vars exist
    // when the first hidden/visible state needs them.
    measureHeights();
    window.addEventListener('resize', measureHeights);
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
